import { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { tailwindClasses } from '@/styles/design-tokens'
import type { VolunteerSlot } from '../types/slot'

interface Props {
  slot: VolunteerSlot
  onSubmit: (
    userName: string,
    email: string,
    phoneNumber: string,
    selectedClass?: string,
    siblingClasses?: string[]
  ) => Promise<void>
  onClose: () => void
}

const formatDisplayDate = (dateStr: string) => {
  const [year, month, day] = dateStr.split('-')
  return `${day}.${month}.${year}`
}

// School grade structure: Grades 1 through 6, max 3 classes per grade
const SCHOOL_GRADES = [
  { grade: 'א', label: 'שכבת א׳', classes: ['כיתה א-1', 'כיתה א-2', 'כיתה א-3'] },
  { grade: 'ב', label: 'שכבת ב׳', classes: ['כיתה ב-1', 'כיתה ב-2', 'כיתה ב-3'] },
  { grade: 'ג', label: 'שכבת ג׳', classes: ['כיתה ג-1', 'כיתה ג-2', 'כיתה ג-3'] },
  { grade: 'ד', label: 'שכבת ד׳', classes: ['כיתה ד-1', 'כיתה ד-2', 'כיתה ד-3'] },
  { grade: 'ה', label: 'שכבת ה׳', classes: ['כיתה ה-1', 'כיתה ה-2', 'כיתה ה-3'] },
  { grade: 'ו', label: 'שכבת ו׳', classes: ['כיתה ו-1', 'כיתה ו-2', 'כיתה ו-3'] },
]

export function RegistrationModal({ slot, onSubmit, onClose }: Props) {
  const { user } = useAuth()
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [email, setEmail] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [hasSiblings, setHasSiblings] = useState(false)
  const [selectedSiblingClasses, setSelectedSiblingClasses] = useState<string[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [timeRemaining, setTimeRemaining] = useState(60)

  const hasInitializedEmail = useRef(false)

  useEffect(() => {
    if (user && !hasInitializedEmail.current) {
      const userEmail = user.email || ''
      setEmail(userEmail)
      hasInitializedEmail.current = true
    }
  }, [user])

  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose

  useEffect(() => {
    // Determine the lock expiration timestamp
    const initialDuration = 60
    const expiresAt = slot.lock_expires_at 
      ? new Date(slot.lock_expires_at).getTime() 
      : Date.now() + initialDuration * 1000

    const updateCountdown = () => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
      setTimeRemaining(remaining)

      if (remaining <= 0) {
        clearInterval(timer)
        onCloseRef.current()
      }
    }

    updateCountdown()
    const timer = setInterval(updateCountdown, 500)

    return () => clearInterval(timer)
  }, [slot.lock_expires_at])

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [])

  const toggleSiblingClass = (className: string) => {
    setSelectedSiblingClasses(prev => {
      if (prev.includes(className)) {
        return prev.filter(c => c !== className)
      } else {
        return [...prev, className]
      }
    })
  }

  const validateForm = () => {
    if (!firstName.trim()) {
      setError('שם פרטי הוא שדה חובה')
      return false
    }

    if (!surname.trim()) {
      setError('שם משפחה הוא שדה חובה')
      return false
    }

    if (!email.trim()) {
      setError('כתובת מייל היא שדה חובה')
      return false
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError('כתובת מייל לא תקינה')
      return false
    }

    if (!phoneNumber.trim()) {
      setError('מספר פלאפון הוא שדה חובה')
      return false
    }

    if (phoneNumber.length !== 10) {
      setError('מספר פלאפון חייב להכיל 10 ספרות')
      return false
    }

    if (!phoneNumber.startsWith('05')) {
      setError('מספר פלאפון ישראלי חייב להתחיל ב-05')
      return false
    }

    const validPrefixes = ['050', '051', '052', '053', '054', '055', '058']
    const prefix = phoneNumber.substring(0, 3)
    if (!validPrefixes.includes(prefix)) {
      setError('קידומת לא תקינה. חייב להיות: 050, 051, 052, 053, 054, 055, או 058')
      return false
    }

    if (hasSiblings && selectedSiblingClasses.length === 0) {
      setError('סימנת שיש ילדים נוספים. אנא בחר לפחות כיתה אחת של אח/ות או בטל את הסימון.')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const fullName = `${firstName.trim()} ${surname.trim()}`
      await onSubmit(
        fullName,
        email.trim(),
        phoneNumber.trim(),
        slot.class_name,
        hasSiblings ? selectedSiblingClasses : []
      )
    } catch (err) {
      setError('שגיאה ברישום. נסה שוב מאוחר יותר')
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    // Lock body scroll on mobile while modal is open
    const originalStyle = window.getComputedStyle(document.body).overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = originalStyle
    }
  }, [])

  return (
    <div
      className="modal-overlay fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCloseRef.current()
      }}
    >
      <div
        className="modal-content w-full max-w-lg max-h-[92dvh] sm:max-h-[85vh] flex flex-col bg-white rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden"
        dir="rtl"
      >
        {/* Grab Handle for Mobile */}
        <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mt-2.5 mb-1 sm:hidden flex-shrink-0" />

        {/* Header */}
        <div className="flex justify-between items-center px-4 py-3 border-b border-gray-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-2 sm:gap-3">
            <h2 className="text-lg font-bold text-gray-900 font-hebrew">
              רישום לתורנות
            </h2>
            <div className="flex items-center gap-1.5 px-3 py-1 bg-blue-100 rounded-full">
              <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-xs sm:text-sm font-semibold text-blue-700 font-hebrew">
                {timeRemaining} שניות
              </span>
            </div>
          </div>
          <button
            onClick={() => onCloseRef.current()}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
            aria-label="סגור"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable Form Body */}
        <div className="overflow-y-auto flex-grow p-4 sm:p-5 space-y-4 overscroll-contain">
          {/* Slot Summary - Sleek banner */}
          <div className="bg-blue-50/90 border border-blue-100 rounded-xl p-3 text-xs sm:text-sm text-blue-900 font-hebrew flex items-center justify-between flex-wrap gap-2 shadow-xs">
            <span className="font-bold flex items-center gap-1.5">
              <span>📅</span> {formatDisplayDate(slot.date)} ({slot.day})
            </span>
            <span className="font-bold bg-blue-100 text-blue-950 px-2.5 py-0.5 rounded-md">
              {slot.class_name || 'כל הכיתות'}
            </span>
            <span className="text-blue-800 font-semibold" dir="ltr">
              ⏰ {slot.time_slot}
            </span>
          </div>

          <form id="registration-form" onSubmit={handleSubmit} className="space-y-3.5">
            {/* Form Fields: Name in 2 columns, Email & Phone full width with 16px font */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-xs font-semibold text-gray-700 font-hebrew mb-1">
                  שם פרטי *
                </label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-hebrew text-right text-base sm:text-sm min-h-[44px]"
                  placeholder="שם פרטי..."
                  disabled={isSubmitting}
                  dir="rtl"
                  autoFocus
                />
              </div>

              <div>
                <label htmlFor="surname" className="block text-xs font-semibold text-gray-700 font-hebrew mb-1">
                  שם משפחה *
                </label>
                <input
                  type="text"
                  id="surname"
                  value={surname}
                  onChange={(e) => setSurname(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 font-hebrew text-right text-base sm:text-sm min-h-[44px]"
                  placeholder="שם משפחה..."
                  disabled={isSubmitting}
                  dir="rtl"
                />
              </div>

              <div className="col-span-2">
                <label htmlFor="email" className="block text-xs font-semibold text-gray-700 font-hebrew mb-1">
                  כתובת מייל *
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left text-base sm:text-sm min-h-[44px]"
                  placeholder="example@gmail.com"
                  disabled={isSubmitting}
                  dir="ltr"
                />
              </div>

              <div className="col-span-2">
                <label htmlFor="phoneNumber" className="block text-xs font-semibold text-gray-700 font-hebrew mb-1">
                  מספר פלאפון *
                </label>
                <input
                  type="tel"
                  id="phoneNumber"
                  value={phoneNumber}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, '')
                    setPhoneNumber(value)
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-right font-hebrew text-base sm:text-sm min-h-[44px]"
                  placeholder="0501234567"
                  disabled={isSubmitting}
                  dir="rtl"
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                />
              </div>
            </div>

            {/* Sibling / Multi-Child Family Tracking Section */}
            <div className="border border-blue-200/80 bg-gradient-to-b from-blue-50/60 to-white rounded-2xl p-3.5 sm:p-4 transition-all shadow-xs">
              <label className="flex items-start gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={hasSiblings}
                  onChange={(e) => {
                    setHasSiblings(e.target.checked)
                    if (!e.target.checked) setSelectedSiblingClasses([])
                  }}
                  className="mt-0.5 h-5 w-5 text-blue-600 rounded-md border-gray-300 focus:ring-blue-500 cursor-pointer"
                />
                <div className="flex-1">
                  <span className="font-bold text-gray-900 font-hebrew text-sm sm:text-base flex items-center gap-1.5">
                    <span>👨‍👩‍👧‍👦</span>
                    יש לנו ילדים נוספים הלומדים בבית הספר אילנות
                  </span>
                  <p className="text-xs text-blue-800/90 font-hebrew mt-1 leading-relaxed">
                    💡 <strong>חשוב לנו לדעת:</strong> כדי לוודא שכל משפחה תבצע <u>תורנות אחת בלבד</u> לאורך כל שנת הלימודים, ולפטור אתכם מתורנות בכיתות של האחים!
                  </p>
                </div>
              </label>

              {/* Class Pills Selector (when checked) */}
              {hasSiblings && (
                <div className="mt-3.5 pt-3.5 border-t border-blue-100 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-bold text-gray-700 font-hebrew">
                      סמנו את הכיתות של הילדים הנוספים:
                    </p>
                    {selectedSiblingClasses.length > 0 && (
                      <span className="text-[11px] font-bold px-2.5 py-0.5 bg-blue-100 text-blue-800 rounded-full font-hebrew">
                        נבחרו: {selectedSiblingClasses.length} כיתות
                      </span>
                    )}
                  </div>

                  <div className="space-y-2">
                    {SCHOOL_GRADES.map(gradeGroup => {
                      return (
                        <div key={gradeGroup.grade} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
                          <span className="text-xs font-bold text-gray-600 font-hebrew w-14 flex-shrink-0">
                            {gradeGroup.label}:
                          </span>
                          <div className="grid grid-cols-3 gap-1.5 flex-1">
                            {gradeGroup.classes.map(className => {
                              const isCurrentClass = slot.class_name === className
                              const isSelected = selectedSiblingClasses.includes(className)

                              if (isCurrentClass) {
                                return (
                                  <div
                                    key={className}
                                    className="py-1.5 px-2 text-center text-xs text-gray-400 bg-gray-100 border border-gray-200 rounded-xl font-hebrew cursor-not-allowed select-none flex items-center justify-center gap-1 min-h-[38px]"
                                    title="כיתת התורנות הנוכחית"
                                  >
                                    <span>{className.replace('כיתה ', '')}</span>
                                    <span className="text-[10px] hidden xs:inline">(נוכחית)</span>
                                  </div>
                                )
                              }

                              return (
                                <button
                                  type="button"
                                  key={className}
                                  onClick={() => toggleSiblingClass(className)}
                                  className={`py-1.5 px-2 text-xs font-hebrew rounded-xl border transition-all duration-150 flex items-center justify-center gap-1 min-h-[38px] sm:min-h-[40px] active:scale-95 touch-manipulation ${
                                    isSelected
                                      ? 'bg-blue-600 text-white border-blue-600 font-bold shadow-sm ring-2 ring-blue-300'
                                      : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400 hover:bg-blue-50/50'
                                  }`}
                                >
                                  <span>{className.replace('כיתה ', '')}</span>
                                  {isSelected && <span className="text-xs">✓</span>}
                                </button>
                              )
                            })}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div className={tailwindClasses.alertError}>
                <svg className={`${tailwindClasses.alertIcon} text-danger-600 flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className={`${tailwindClasses.alertText} text-danger-800 text-xs sm:text-sm`}>{error}</p>
                </div>
              </div>
            )}

            {timeRemaining <= 10 && timeRemaining > 0 && (
              <div className={tailwindClasses.alertWarning}>
                <svg className={`${tailwindClasses.alertIcon} text-warning-600 animate-pulse flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <p className={`${tailwindClasses.alertText} text-warning-700 text-xs sm:text-sm`}>
                  הזמן אוזל! {timeRemaining} שניות נותרו
                </p>
              </div>
            )}
          </form>
        </div>

        {/* Sticky Action Footer (Thumb-friendly) */}
        <div className="p-3.5 sm:p-4 border-t border-gray-100 flex-shrink-0 bg-white flex items-center justify-between gap-3 pb-[max(1rem,calc(env(safe-area-inset-bottom,0px)+0.5rem))]">
          <button
            type="button"
            onClick={() => onCloseRef.current()}
            disabled={isSubmitting}
            className="px-4 py-2.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-hebrew font-medium disabled:opacity-50 min-h-[48px] min-w-[80px]"
          >
            ביטול
          </button>
          <button
            type="submit"
            form="registration-form"
            disabled={isSubmitting || !firstName.trim() || !surname.trim() || !email.trim() || !phoneNumber.trim()}
            className="flex-1 px-5 py-2.5 text-base bg-blue-600 hover:bg-blue-700 text-white rounded-xl transition-all font-hebrew font-bold disabled:opacity-50 flex items-center justify-center gap-2 shadow-lg active:scale-95 min-h-[48px] touch-manipulation"
          >
            {isSubmitting ? (
              <>
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                <span>רושם...</span>
              </>
            ) : (
              'הירשם לתורנות'
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
